/**
 * Pure helpers behind the model page's "Scheduled replicas" band (TRA-1426),
 * kept out of the components so they are testable without a DOM.
 *
 * A window lives on the Baseten environment the model serves from. The api's
 * PUT takes the FULL list of weekday windows to keep (one with an id is
 * replaced, one without is created, one left out is deleted), so every change
 * made here is "the current list with one difference". Baseten enforces the
 * rules between windows (no overlap, a gap of at least 5 minutes before the
 * regular range returns) and the api relays its sentence; what the browser
 * can check about one window on its own is here.
 */

import { z } from 'zod'

import type {
  ModelScheduleWindow,
  ModelScheduleWindowUpdate,
  ModelSchedulesResponse,
  ModelSchedulesUpdate,
  ReplicaSettingChangeResponse,
  Weekday,
} from '@/hooks/useModels'

/** Baseten's cap on one environment. */
export const MAX_WINDOWS = 10
const MIN_WINDOW_MINUTES = 5
const MINUTES_PER_DAY = 24 * 60
const MAX_REPLICAS = 64

const DAY_VALUES = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const satisfies readonly Weekday[]

/** Monday first: the order days are shown in and sent in. */
export const WEEKDAYS: readonly { value: Weekday; label: string }[] = [
  { value: 'MONDAY', label: 'Mon' },
  { value: 'TUESDAY', label: 'Tue' },
  { value: 'WEDNESDAY', label: 'Wed' },
  { value: 'THURSDAY', label: 'Thu' },
  { value: 'FRIDAY', label: 'Fri' },
  { value: 'SATURDAY', label: 'Sat' },
  { value: 'SUNDAY', label: 'Sun' },
]

const CADENCE_LABELS: Record<string, string> = {
  DAILY: 'Weekly',
  HOURLY: 'Hourly',
  ONE_TIME: 'One time',
}

export function cadenceLabel(cadence: string): string {
  return CADENCE_LABELS[cadence] ?? cadence
}

// ---------------------------------------------------------------------------
// Reading the environment's windows
// ---------------------------------------------------------------------------

/** A weekday window the environment already has, in the shape the PUT takes. */
export type ExistingWindow = ModelScheduleWindowUpdate & { id: string }

/**
 * A window this page can edit, in the shape the PUT takes, or null for one it
 * cannot: a window of another cadence (hourly or one-time, set on Baseten),
 * which the api lists and leaves alone.
 *
 * The knobs this page does not edit travel back unchanged. A PUT replaces
 * the whole window, and a knob left out would reset to the environment's.
 */
export function toWindowUpdate(source: ModelScheduleWindow): ExistingWindow | null {
  if (source.cadence !== 'DAILY' || source.id === null) return null
  const { start_hour, start_minute, end_hour, end_minute, min_replica, max_replica } = source
  if (start_hour === null || start_minute === null) return null
  if (end_hour === null || end_minute === null) return null
  if (min_replica === null || max_replica === null) return null
  const weekdays = DAY_VALUES.filter((day) => source.weekdays.includes(day))
  if (weekdays.length === 0 || weekdays.length !== source.weekdays.length) return null
  return {
    id: source.id,
    name: source.name,
    enabled: source.enabled,
    weekdays,
    start_hour,
    start_minute,
    end_hour,
    end_minute,
    min_replica,
    max_replica,
    autoscaling_window: source.autoscaling_window,
    scale_down_delay: source.scale_down_delay,
    max_scale_down_rate: source.max_scale_down_rate,
    concurrency_target: source.concurrency_target,
    target_utilization_percentage: source.target_utilization_percentage,
  }
}

/**
 * Every weekday window as the PUT takes it, or null when one of them cannot
 * be read. A save that left that one out would delete it, so the page then
 * offers no edits at all.
 */
export function editableWindows(schedules: ModelSchedulesResponse): ExistingWindow[] | null {
  const windows: ExistingWindow[] = []
  for (const source of schedules.schedules) {
    if (source.cadence !== 'DAILY') continue
    const existing = toWindowUpdate(source)
    if (existing === null) return null
    windows.push(existing)
  }
  return windows
}

// ---------------------------------------------------------------------------
// One change to the list
// ---------------------------------------------------------------------------

export type WindowChange =
  /** Replace the window with this id, or add it when it has none yet. */
  | { kind: 'save'; saved: ModelScheduleWindowUpdate; timezone: string }
  | { kind: 'remove'; id: string }
  | { kind: 'enable'; id: string; enabled: boolean }

/**
 * The schedule as the page last read it: what a save is built on. The
 * `revision` goes back with the save, and the api refuses it (409) when the
 * schedule has changed since, so a list this page no longer has right can
 * never overwrite the real one.
 */
export interface ScheduleSnapshot {
  windows: readonly ExistingWindow[]
  /** The environment's timezone, or the browser's before a window exists. */
  timezone: string
  revision: string
}

/** A dialog the band has open. */
export type ScheduleDialog =
  | { kind: 'closed' }
  | { kind: 'add' }
  | { kind: 'edit'; entry: ExistingWindow; snapshot: ScheduleSnapshot }
  | { kind: 'remove'; entry: ExistingWindow; snapshot: ScheduleSnapshot }

/**
 * The read a change is built on.
 *
 * A new window, and a switch in the table, act on the schedule as it is now.
 * An open edit or delete acts on one window AS IT WAS when the dialog opened:
 * the dialog holds a copy of it, and the list can refresh behind the dialog
 * (the 60s poll, or the reload after a refused save). Built on that newer
 * revision, the save would pass the api's check and put the dialog's old
 * copy back over whatever someone else changed on the window, including the
 * knobs this page does not show. Built on the revision it opened under, the
 * same save is refused, and the band closes the dialog on the fresh list.
 */
export function baseFor(dialog: ScheduleDialog, current: ScheduleSnapshot): ScheduleSnapshot {
  return dialog.kind === 'edit' || dialog.kind === 'remove' ? dialog.snapshot : current
}

function changedWindows(
  windows: readonly ExistingWindow[],
  change: WindowChange,
): ModelScheduleWindowUpdate[] {
  switch (change.kind) {
    case 'save': {
      const { saved } = change
      return windows.some((w) => w.id === saved.id)
        ? windows.map((w) => (w.id === saved.id ? saved : w))
        : [...windows, saved]
    }
    case 'remove':
      return windows.filter((w) => w.id !== change.id)
    case 'enable':
      return windows.map((w) => (w.id === change.id ? { ...w, enabled: change.enabled } : w))
  }
}

/** The PUT body for one change: the snapshot's list with that difference. */
export function applyChange(snapshot: ScheduleSnapshot, change: WindowChange): ModelSchedulesUpdate {
  return {
    timezone: change.kind === 'save' ? change.timezone : snapshot.timezone,
    revision: snapshot.revision,
    schedules: changedWindows(snapshot.windows, change),
  }
}

// ---------------------------------------------------------------------------
// The add/edit form
// ---------------------------------------------------------------------------

/** The dialog's fields as the inputs hold them, before validation. */
export interface WindowDraft {
  name: string
  weekdays: Weekday[]
  /** `HH:MM`, as `<input type="time">` holds it. */
  start: string
  end: string
  minReplica: string
  maxReplica: string
  timezone: string
}

export type WindowField = keyof WindowDraft
export type WindowErrors = Partial<Record<WindowField, string>>

/** The checked window, or each field's first problem. */
export type ParsedWindow =
  | { kind: 'ok'; saved: ModelScheduleWindowUpdate; timezone: string }
  | { kind: 'error'; errors: WindowErrors }

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/

/** The hour and minute of an `HH:MM`. */
function clock(time: string): { hour: number; minute: number } {
  return { hour: Number(time.slice(0, 2)), minute: Number(time.slice(3, 5)) }
}

function minutesOf(time: string): number {
  const { hour, minute } = clock(time)
  return hour * 60 + minute
}

/** How long a window runs. Baseten lets one wrap past midnight. */
export function windowMinutes(start: string, end: string): number {
  return (minutesOf(end) - minutesOf(start) + MINUTES_PER_DAY) % MINUTES_PER_DAY
}

function replicaCount(label: string, min: number): z.ZodType<number, z.ZodTypeDef, string> {
  const message = `${label} must be a whole number from ${String(min)} to ${String(MAX_REPLICAS)}.`
  return z
    .string()
    .trim()
    .regex(/^\d+$/, message)
    .transform(Number)
    .pipe(z.number().min(min, message).max(MAX_REPLICAS, message))
}

const windowSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name the window.')
    .max(120, 'A name holds up to 120 characters.'),
  weekdays: z.array(z.enum(DAY_VALUES)).min(1, 'Pick at least one day.'),
  start: z.string().regex(TIME, 'Set a start time.'),
  end: z.string().regex(TIME, 'Set an end time.'),
  minReplica: replicaCount('Min replicas', 0),
  maxReplica: replicaCount('Max replicas', 1),
  timezone: z.string().min(1, 'Pick a timezone.'),
})

type WindowForm = z.infer<typeof windowSchema>

/**
 * What only shows once every field is valid on its own: the rules between
 * two fields, and the name against the environment's other windows. Baseten
 * wants every name unique, and one the browser can already see is taken
 * fails here instead of in a toast.
 */
function crossFieldErrors(form: WindowForm, others: readonly ExistingWindow[]): WindowErrors {
  const errors: WindowErrors = {}
  if (others.some((w) => w.name === form.name)) {
    errors.name = 'Another window has this name.'
  }
  if (form.minReplica > form.maxReplica) {
    errors.minReplica = 'Min replicas cannot exceed max replicas.'
  }
  if (form.start === form.end) {
    errors.end = 'The end time must differ from the start time.'
  } else if (windowMinutes(form.start, form.end) < MIN_WINDOW_MINUTES) {
    errors.end = `A window lasts at least ${String(MIN_WINDOW_MINUTES)} minutes.`
  }
  return errors
}

const FIELDS = [
  'name',
  'weekdays',
  'start',
  'end',
  'minReplica',
  'maxReplica',
  'timezone',
] as const satisfies readonly WindowField[]

function isField(value: unknown): value is WindowField {
  return FIELDS.some((field) => field === value)
}

function firstErrors(issues: readonly z.ZodIssue[]): WindowErrors {
  const errors: WindowErrors = {}
  for (const issue of issues) {
    const field = issue.path[0]
    if (isField(field) && !(field in errors)) errors[field] = issue.message
  }
  return errors
}

/**
 * Check the dialog's fields. `editing` is the window being changed (its id,
 * its on or off state and the knobs this page does not edit carry over),
 * null to add one. `others` are the environment's other windows.
 */
export function parseWindowDraft(
  draft: WindowDraft,
  editing: ExistingWindow | null,
  others: readonly ExistingWindow[],
): ParsedWindow {
  const result = windowSchema.safeParse(draft)
  if (!result.success) return { kind: 'error', errors: firstErrors(result.error.issues) }
  const form = result.data
  const errors = crossFieldErrors(form, others)
  if (Object.keys(errors).length > 0) return { kind: 'error', errors }
  const start = clock(form.start)
  const end = clock(form.end)
  return {
    kind: 'ok',
    timezone: form.timezone,
    saved: {
      ...editing,
      id: editing?.id ?? null,
      name: form.name,
      enabled: editing?.enabled ?? true,
      weekdays: DAY_VALUES.filter((day) => form.weekdays.includes(day)),
      start_hour: start.hour,
      start_minute: start.minute,
      end_hour: end.hour,
      end_minute: end.minute,
      min_replica: form.minReplica,
      max_replica: form.maxReplica,
    },
  }
}

/** `HH:MM` for a time input. */
export function timeValue(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** The dialog's starting fields: the window being edited, or a blank weekday window. */
export function draftFrom(editing: ExistingWindow | null, timezone: string): WindowDraft {
  if (editing === null) {
    return {
      name: '',
      weekdays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
      start: '',
      end: '',
      minReplica: '',
      maxReplica: '',
      timezone,
    }
  }
  return {
    name: editing.name,
    weekdays: [...editing.weekdays],
    start: timeValue(editing.start_hour, editing.start_minute),
    end: timeValue(editing.end_hour, editing.end_minute),
    minReplica: String(editing.min_replica),
    maxReplica: String(editing.max_replica),
    timezone,
  }
}

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

/** `Every day`, `Mon to Fri` for a run of three or more, else `Mon, Wed`. */
export function formatDays(weekdays: readonly string[]): string {
  const picked = WEEKDAYS.filter((day) => weekdays.includes(day.value))
  if (picked.length === WEEKDAYS.length) return 'Every day'
  const first = picked[0]
  const last = picked[picked.length - 1]
  if (first === undefined || last === undefined) return ''
  const from = WEEKDAYS.indexOf(first)
  const isRun = picked.every((day, i) => WEEKDAYS[from + i] === day)
  if (isRun && picked.length >= 3) return `${first.label} to ${last.label}`
  return picked.map((day) => day.label).join(', ')
}

/** `6:40 AM`. */
export function formatClock(hour: number, minute: number): string {
  const period = hour < 12 ? 'AM' : 'PM'
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12
  return `${String(twelveHour)}:${String(minute).padStart(2, '0')} ${period}`
}

/** The four clock fields of a window, as every window shape carries them. */
export type WindowHours = Pick<
  ModelScheduleWindowUpdate,
  'start_hour' | 'start_minute' | 'end_hour' | 'end_minute'
>

/** `6:40 AM to 7:45 AM`, with `next day` when the window wraps past midnight. */
export function formatHours(hours: WindowHours): string {
  const start = timeValue(hours.start_hour, hours.start_minute)
  const end = timeValue(hours.end_hour, hours.end_minute)
  const range = `${formatClock(hours.start_hour, hours.start_minute)} to ${formatClock(hours.end_hour, hours.end_minute)}`
  return end <= start ? `${range} next day` : range
}

/** `7 to 10`, or `3` when the range is one number. */
export function formatReplicas(min: number, max: number): string {
  return min === max ? String(min) : `${String(min)} to ${String(max)}`
}

// ---------------------------------------------------------------------------
// Timezones
// ---------------------------------------------------------------------------

/**
 * The zones the picker offers. Baseten takes any IANA name; this is a short
 * list of the common ones, and the picker adds the environment's current
 * zone and the browser's own, so the two a person is most likely to want are
 * always there.
 */
const COMMON_TIMEZONES: readonly string[] = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Rome',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
]

export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * 'Oct 21, 6:52 AM': when a replica setting was saved (TRA-1558), in
 * Pacific time, where the people reading the list sit.
 */
/**
 * Who a change is from: the email of the person who saved it on this page,
 * or Baseten when the api's checker found it there (TRA-1558 step 2). A
 * row with no email has no person behind it either way.
 */
export function changeAuthor(
  change: Pick<ReplicaSettingChangeResponse, 'source' | 'changed_by_email'>,
): string {
  if (change.source === 'baseten' || change.changed_by_email === null) return 'changed in Baseten'
  return change.changed_by_email
}

export function formatChangeTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** The common zones plus any given ones not already among them, given ones first. */
export function timezoneOptions(...zones: readonly string[]): string[] {
  const extra = zones.filter((zone) => !COMMON_TIMEZONES.includes(zone))
  return [...new Set([...extra, ...COMMON_TIMEZONES])]
}
